import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getMaintenanceStatus } from "./api";

const MAINTENANCE_RETRY_STORAGE_KEY = "maintenance_retry_after_iso";
const POLL_INTERVAL_MS = 30_000;

function formatRetryAfter(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

const MaintenancePage: React.FC = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [retryAfterText, setRetryAfterText] = useState<string | null>(null);

  useEffect(() => {
    function checkStatus(): void {
      getMaintenanceStatus()
        .then((data) => {
          if (!data.maintenance_active) {
            sessionStorage.removeItem(MAINTENANCE_RETRY_STORAGE_KEY);
            navigate("/", { replace: true });
            return;
          }
          const iso =
            data.retry_after_iso ??
            sessionStorage.getItem(MAINTENANCE_RETRY_STORAGE_KEY);
          if (iso) {
            setRetryAfterText(formatRetryAfter(iso));
          }
        })
        .catch(() => {});
    }

    checkStatus();
    const interval = setInterval(checkStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [navigate]);

  const storedIso = sessionStorage.getItem(MAINTENANCE_RETRY_STORAGE_KEY);
  const displayRetry =
    retryAfterText ?? (storedIso ? formatRetryAfter(storedIso) : null);

  return (
    <div className="h-screen bg-cp-black flex flex-col justify-center items-center p-20">
      <div className="max-w-700 mx-auto text-align-center">
        {/* Logo/Icon */}
        <div className="mb-20">
          <img
            src="/maintenance.svg"
            alt={t("maintenanceAlt")}
            className="w-150 h-150 mx-auto mb-20"
          />
        </div>

        {/* Main Content */}
        <div className="mb-40">
          <h1 className="text-4xl font-bold text-cp-white mb-20">
            {t("maintenanceTitle")}
          </h1>

          <p className="text-xl text-muted-text-gray mb-20">
            {t("maintenanceDescription")}
          </p>

          {displayRetry && (
            <p className="text-lg text-cp-yellow font-w-600 mb-20">
              {t("maintenanceExpectedBack", { time: displayRetry })}
            </p>
          )}

          <p className="text-lg text-muted-text-gray">
            {t("maintenanceCheckBackSoon")}
          </p>
        </div>

        {/* Status Indicator */}
        <div className="mt-40 flex items-center justify-center gap-10">
          <div className="w-20 h-20 bg-cp-yellow rounded-full"></div>
          <span className="text-cp-yellow font-w-600">
            {t("maintenanceInProgress")}
          </span>
        </div>

        {/* Contact Information */}
        <div className="mt-40 p-20 bg-dark-gray border-radius-10 border-light-gray">
          <h3 className="text-lg font-w-600 text-cp-white mb-10">
            {t("maintenanceNeedHelp")}
          </h3>
          <p className="text-muted-text-gray mb-20">
            {t("maintenanceContactUs")}
          </p>
          <div className="flex flex-col gap-10">
            <p className="text-cp-white">
              <span className="text-muted-text-gray">{t("maintenanceEmailLabel")}:</span>
              <span className="ml-10 text-cp-yellow">
                support@clearpath.com
              </span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-40 text-align-center">
          <p className="text-sm text-muted-text-gray">
            {t("maintenanceCopyright")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
